import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { io } from 'socket.io-client';
import {
  CHANNELS,
  PROTOCOL_VERSION,
  SYSTEM_EVENTS,
  type DirectoryMember,
  type HelloPayload,
  type StageStatePayload,
  type ViewerEvent,
} from '@efira/protocol';

import { apiUrl } from '../auth/client';

/**
 * Живой эфир в консоли: одна подписка на всю правую колонку.
 *
 * ПОЧЕМУ ОДИН СОКЕТ НА КОНСОЛЬ, А НЕ ПО ОДНОМУ НА СЕКЦИЮ. Счётчики, лента
 * чата и лента гифтеров смотрят в ОДИН поток событий. Три подписки означали
 * бы три соединения, три копии буфера и три разных представления о том, что
 * сейчас в эфире, - расхождение вылезло бы у стримера в виде «в счётчике 12
 * комментариев, в ленте 11».
 *
 * БУФЕР ОГРАНИЧЕН. Восемьдесят строк - столько же, сколько у эталона:
 * четырёхчасовой эфир на тысяче зрителей даёт десятки тысяч событий, и лента
 * без предела съедает память вкладки, в которой стример работает весь эфир.
 *
 * СЧЁТЧИКИ СЧИТАЮТСЯ ЗДЕСЬ, А НЕ ПРИХОДЯТ ЧИСЛОМ. Сервер и так шлёт каждое
 * событие; отдельный пакет со счётчиками был бы вторым источником правды о
 * том же самом, и он бы разошёлся с лентой.
 */
export const CHAT_BUFFER = 80;

/**
 * Окно склейки повторов - ЧИСЛОМ В ОДНОМ МЕСТЕ.
 *
 * Находка владельца по парам: восемь подряд строк «Имя поставил лайков: 1».
 * В настоящем эфире лента забивается лайками за секунды и перестаёт быть
 * лентой. Повторы одного зрителя одного вида, пришедшие подряд в это окно,
 * складываются в одну строку со счётчиком.
 *
 * КОММЕНТАРИИ НЕ СКЛЕИВАЮТСЯ НИКОГДА: это разные слова, и «×3» вместо трёх
 * сообщений - потеря содержания, а не экономия места.
 */
export const MERGE_WINDOW_MS = 15_000;

export interface ChatLineItem {
  id: string;
  type: ViewerEvent['type'];
  actorId: string;
  at: number;
  /** Текст комментария. У остальных типов строка составляется по типу. */
  text?: string;
  /** Подарок: сколько монет и сколько штук. */
  coins?: number;
  count?: number;
  giftId?: string;
}

export interface StreamCounters {
  viewers: number;
  comments: number;
  likes: number;
  gifts: number;
  coins: number;
  followers: number;
}

export interface ConsoleStream {
  /** Идёт ли поток: сокет подключён. */
  connected: boolean;
  counters: StreamCounters;
  /** Лента событий, свежие в конце. */
  chat: ChatLineItem[];
  /** Подарки, свежие в конце. */
  gifts: ChatLineItem[];
  /** Справочник зрителей: имя и аватар приходят один раз. */
  members: Readonly<Record<string, DirectoryMember>>;
  /**
   * Что играет в ОВЕРЛЕЕ.
   *
   * Приходит от него же (`stage:state`), а не считается здесь: полоса,
   * считающая время сама, разошлась бы с эфиром на первой паузе.
   */
  music: StageStatePayload['music'] | null;
}

/**
 * Склейка повтора с последней строкой ленты.
 *
 * Складывается ТОЛЬКО с последней: «Аня лайк, Боря лайк, Аня лайк» - это
 * три строки, а не две. Иначе лента перестаёт быть хронологией.
 */
export function mergeLine(lines: ChatLineItem[], line: ChatLineItem): ChatLineItem[] {
  const last = lines[lines.length - 1];

  const mergeable =
    last !== undefined &&
    line.type !== 'comment' &&
    last.type === line.type &&
    last.actorId === line.actorId &&
    line.at - last.at <= MERGE_WINDOW_MS;

  if (!mergeable) {
    return [...lines, line];
  }

  return [
    ...lines.slice(0, -1),
    {
      ...last,
      at: line.at,
      count: (last.count ?? 1) + (line.count ?? 1),
      coins: (last.coins ?? 0) + (line.coins ?? 0),
      /* Идентификатор берётся у свежего события: строка обновилась, и
         React обязан это увидеть. */
      id: line.id,
    },
  ];
}

const EMPTY_COUNTERS: StreamCounters = {
  viewers: 0,
  comments: 0,
  likes: 0,
  gifts: 0,
  coins: 0,
  followers: 0,
};

const StreamContext = createContext<ConsoleStream>({
  connected: false,
  counters: EMPTY_COUNTERS,
  chat: [],
  gifts: [],
  members: {},
  music: null,
});

export function useConsoleStream(): ConsoleStream {
  return useContext(StreamContext);
}

export function ConsoleStreamProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false);
  const [chat, setChat] = useState<ChatLineItem[]>([]);
  const [gifts, setGifts] = useState<ChatLineItem[]>([]);
  const [members, setMembers] = useState<Readonly<Record<string, DirectoryMember>>>({});
  const [counters, setCounters] = useState<StreamCounters>(EMPTY_COUNTERS);
  const [music, setMusic] = useState<StageStatePayload['music'] | null>(null);

  /* Кто уже проявился: «зрители» - это РАЗНЫЕ участники, а не число событий.
     Множество живёт в рефе: оно не рисуется и не должно вызывать рендер. */
  const seen = useRef(new Set<string>());

  useEffect(() => {
    const socket = io(apiUrl(''), { transports: ['websocket'], withCredentials: true });

    socket.on('connect', () => {
      setConnected(true);
      socket.emit(SYSTEM_EVENTS.hello, {
        protocol: PROTOCOL_VERSION,
        role: 'dashboard',
      } satisfies HelloPayload);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on(CHANNELS.directoryMembers, (raw: unknown) => {
      const payload = raw as { members?: Record<string, DirectoryMember> } | null;

      if (payload?.members === undefined) {
        return;
      }

      setMembers((current) => ({ ...current, ...payload.members }));
    });

    socket.on(CHANNELS.viewerEvent, (raw: unknown) => {
      const event = raw as ViewerEvent | null;

      if (event === null || typeof event !== 'object' || !('type' in event)) {
        return;
      }

      const line: ChatLineItem = {
        id: event.id,
        type: event.type,
        actorId: event.actor.id,
        at: event.at,
        ...(event.type === 'comment' ? { text: event.text } : {}),
        ...(event.type === 'gift'
          ? { coins: event.coins, count: event.count, giftId: event.giftId }
          : {}),
      };

      /* Лента - ВСЕ события зрителя, а не только комментарии: у эталона так
         же, и это верно - вход и репост тоже повод сказать спасибо. */
      setChat((current) => mergeLine(current, line).slice(-CHAT_BUFFER));

      if (event.type === 'gift') {
        setGifts((current) => [...current, line].slice(-CHAT_BUFFER));
      }

      const isNew = !seen.current.has(event.actor.id);
      seen.current.add(event.actor.id);

      setCounters((current) => ({
        viewers: current.viewers + (isNew ? 1 : 0),
        comments: current.comments + (event.type === 'comment' ? 1 : 0),
        likes: current.likes + (event.type === 'like' ? event.count : 0),
        gifts: current.gifts + (event.type === 'gift' ? event.count : 0),
        coins: current.coins + (event.type === 'gift' ? event.coins * event.count : 0),
        followers: current.followers + (event.type === 'follow' ? 1 : 0),
      }));
    });

    /* Состояние сцены платформы: положение трека и признак игры. Приходит
       с оверлея через сервер - не чаще раза в секунду. */
    socket.on(CHANNELS.stageState, (raw: unknown) => {
      const payload = raw as StageStatePayload | null;

      if (payload?.music !== undefined) {
        setMusic(payload.music);
      }
    });

    return () => {
      socket.close();
    };
  }, []);

  const value = useMemo<ConsoleStream>(
    () => ({ connected, counters, chat, gifts, members, music }),
    [connected, counters, chat, gifts, members, music],
  );

  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>;
}
